import { ReactNode } from 'react';
import {
  AutocompleteRenderInputParams,
  InputAdornment,
  InputBaseProps,
  SxProps,
} from '@mui/material';

import { TextField, TextFieldProps } from '../../TextField';

export interface RenderTextFieldParams {
  autocompleteRenderInputParams: AutocompleteRenderInputParams;
  hasCounter?: boolean;
  hasPlaceholder: boolean;
  maxLength?: number;
  offset: number;
  textFieldProps?: Partial<TextFieldProps>;
}

const endAdornmentSxProps: SxProps = {
  position: 'absolute',
  bottom: 12,
  right: 16,
};

export function renderTextField({
  autocompleteRenderInputParams: params,
  hasCounter,
  hasPlaceholder,
  maxLength,
  offset,
  textFieldProps,
}: RenderTextFieldParams): ReactNode {
  const hasLimit = Boolean(maxLength);

  // Since maxLength here is obviously non-nullable
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const isLimitReached = hasLimit && offset >= maxLength!;

  const endAdornment = hasCounter && hasLimit && (
    <InputAdornment position="end" sx={endAdornmentSxProps}>
      {offset}/{maxLength}
    </InputAdornment>
  );

  const inputProps: InputBaseProps['inputProps'] = {
    ...textFieldProps?.inputProps,
    ...params.inputProps,
    style: { marginRight: 30 },
    // to forbid typing when the limit has reached
    maxLength: isLimitReached ? 0 : undefined,
  };

  const placeholder = hasPlaceholder ? textFieldProps?.placeholder : undefined;

  return (
    <TextField
      {...textFieldProps}
      {...params}
      InputProps={{ ...params.InputProps, endAdornment }}
      // TextField component accepts two different sets of props:
      // - InputProps - props applied to the Input component
      // - inputProps - props passed directly to the html element
      // They are different and written with different names but
      // Eslint considers them (names) like identical.
      //
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={inputProps}
      multiline
      placeholder={placeholder}
    />
  );
}
